import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams, useLocation, useCallback } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Rodal from 'rodal';
import Page from '../../../layout/Page/Page';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import { userAuth } from '../../../contexts/userContext';
import './Login.css';
import Icon from '../../../components/icon/Icon';
import 'rodal/lib/rodal.css';
import { Ralert } from '../../common/vhelper/Normarodallalert';
import Loader from '../../common/vhelper/Loader';
import CountryFlagWithPhoneCodeSelector from '../demo-pages/helper/flag';
import PhoneNumberInput from '../../../components/common/PhoneNumberInput';
import '../demo-pages/helper/flag.css';
import apiServicewt from '../../../api/apiServicewt';
import LANG, { getLangWithKey } from '../../../lang';
import { useTranslation } from 'react-i18next';
import { HeaderRight } from '../../../layout/Header/Header';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import showNotification from '../../../components/extras/showNotification';
import Popovers from '../../../components/bootstrap/Popovers';
import apiService from '../../../api/apiService';
import { useLanguageSwitch } from '../../../contexts/LanguageSwitchContext';
import { useLoginSettings } from '../../../contexts/loginSettingsContext';
import { getConfig } from '../../../config.js';
import { dashboardMenu, demoPages } from '../../../menu';
import { serEmp } from '../../../contexts/serempContext';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { getClientSlug } from '../../../helpers/clientSlug';
import { GoogleLogin } from '@react-oauth/google';

const LoginHeader = ({ isNewUser }) => {
	const { t } = useTranslation();

	if (isNewUser) {
		return (
			<>
				<div className='text-center h3 fw-bold mt-2'>{t('Create Account')}</div>
			</>
		);
	}
	return (
		<>
			<div className='text-center h1 fw-bold mt-3'>{t('Welcome,')}</div>
			<div className='text-center h4 text-muted mb-2'>{t('Sign in to continue!')}</div>
		</>
	);
};

const Login = ({ isSignUp }) => {
	const { i18n } = useTranslation();
	const { t } = useTranslation();
	const { isLangSwitchEnabled } = useLanguageSwitch();
	const { loginSettings, loginSettingsLang, loading: loginSettingsLoading } = useLoginSettings();
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const [usernameInput, setUsernameInput] = useState('');
	const [loginType, setLoginType] = useState('');
	const [isNewUser, setIsNewUser] = useState(false);
	const { serviceidcont, setServiceidcont } = useContext(serEmp);
	const [showPassword, setShowPassword] = useState(false);

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};
	const [cshowPassword, setCshowPassword] = useState(false);

	const ctogglePasswordVisibility = () => {
		setCshowPassword(!cshowPassword);
	};
	const [lshowPassword, setLshowPassword] = useState(false);

	const [qparam] = useSearchParams();
	const [isDemoMode, setIsDemoMode] = useState(false);

	const lemailRef = useRef();
	const lpasswordRef = useRef();
	const lphoneRef = useRef();
	const lphonePasswordRef = useRef();
	const [isempty2, setIsempty2] = useState(false);
	const [loadalert, setLoadalert] = useState(false);
	const [msgloadalert, setMsgLoadalert] = useState('');
	const [msgtypeloadalert, setMsgtypeLoadalert] = useState('');
	const [loaderv, setLoaderv] = useState(true);

	const handleSuccess = async (credentialResponse) => {
		setIsLoading(true);
		try {
			const weblink = getConfig().REACT_APP_WEB_LINK;
			const slug = getClientSlug();

			const item = {
				action: 'login_with_google',
				google_token: credentialResponse.credential,
			};
			const payload = slug ? { ...item, slug: slug } : item;

			const res = await fetch(weblink, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
				body: JSON.stringify(payload),
			});
			const result = await res.json();

			if (result.type === 'Success' || result.type === 'success') {
				if (result.data.userrole === 'cus') {
					setToken(result.data);
					localStorage.setItem('user-auth', JSON.stringify(result.data));
					fetchBranches(result.data);

					if (cameForLogin && serviceId) {
						goToCalendar(serviceId);
					} else {
						navigate('/services');
					}
				} else {
					startmodel();
					setMsgLoadalert(result.message);
					setMsgtypeLoadalert(result.type);
				}
			} else {
				startmodel();
				setMsgLoadalert(result.message);
				setMsgtypeLoadalert(result.type);
			}
		} catch (error) {
			console.error('Google login error:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleError = () => {
		startmodel();
		setMsgLoadalert(t('Google login failed. Please try again.'));
		setMsgtypeLoadalert('error');
	};

	useEffect(() => {
		const demoParam = qparam.get('demo');
		if (demoParam === 'true' || demoParam === '1') {
			setIsDemoMode(true);
			localStorage.setItem('demoMode', 'true');
		} else {
			setIsDemoMode(false);
			localStorage.removeItem('demoMode');
		}
	}, [qparam]);

	const styledBtn = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	const loginBgFromSettings = loginSettings?.background_image;
	const defaultLoginBg = `${process.env.PUBLIC_URL}/assets/img/abstract/Main_a.jpg`;
	const loginBg =
		loginBgFromSettings && String(loginBgFromSettings).trim()
			? loginBgFromSettings
			: defaultLoginBg;

	const [windowWidth, setWindowWidth] = useState(window.innerWidth);

	useEffect(() => {
		document.body.classList.add('auth-page');
		return () => document.body.classList.remove('auth-page');
	}, []);

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const mobileres = windowWidth < 500 ? 'ab' : 'cd';

	useEffect(() => {
		if (qparam.get('code')) {
			const wcode = qparam.get('code');
			const cresult = JSON.parse(atob(wcode));
			setLoadalert(true);
			setMsgLoadalert(cresult.message);
			setMsgtypeLoadalert(cresult.type);
		}
	}, [qparam.get('code')]);

	useEffect(() => {
		if (!loaderv && isDemoMode) {
			const timer = setTimeout(() => {
				if (lemailRef.current && lpasswordRef.current) {
					lemailRef.current.value = 'employee@interflext.com';
					lpasswordRef.current.value = 'Demo123@';
				}
			}, 200);

			return () => clearTimeout(timer);
		}
	}, [isDemoMode, loaderv]);

	useEffect(() => {
		try {
			const typeParam = (qparam.get('type') || '').toLowerCase();
			if (typeParam === 'reg' || typeParam === 'signup' || typeParam === 'register') {
				if (registrationAllow) setIsNewUser(true);
			} else if (typeParam === 'login') {
				setIsNewUser(false);
			}
		} catch (e) {}
	}, [qparam]);

	const ltogglePasswordVisibility = () => {
		setLshowPassword(!lshowPassword);
	};

	const fnameRef = useRef();
	const mnameRef = useRef();
	const lnameRef = useRef();
	const emailRef = useRef();
	const phonecRef = useRef();
	const phoneRef = useRef();
	const passwordRef = useRef();
	const conpasswordRef = useRef();

	const [isemptyfn, setIsemptyfn] = useState({ error: false, msg: t('*Required') });
	const [isemptymn, setIsemptymn] = useState({ error: false, msg: t('*Required') });
	const [isemptyln, setIsemptyln] = useState({ error: false, msg: t('*Required') });
	const [isemptye, setIsemptye] = useState({ error: false, msg: t('*Required') });
	const [isemptypc, setIsemptypc] = useState({ error: false, msg: t('*Required') });
	const [isemptypn, setIsemptypn] = useState({ error: false, msg: t('*Required') });
	const [isemptypw, setIsemptypw] = useState({ error: false, msg: t('*Required') });
	const [isemptycp, setIsemptycp] = useState({ error: false, msg: t('*Required') });

	const [ppcode, setppcode] = useState({});
	const [signupPhoneValue, setSignupPhoneValue] = useState('');

	const [tableOption, setTableOption] = useState([]);

	const u_class = '.gt_container--hich1m';

	const location = useLocation();
	const navigate = useNavigate();

	const cameForLogin = location.state?.type === 'login';
	const serviceId = location.state?.service_id;

	const [isMobile, setIsMobile] = useState(false);

	const resetLoginErrors = () => {
		setIsemptym({ error: false, msg: t('*Required') });
		setIsemptyp({ error: false, msg: t('*Required') });
		setIsemptyphone({ error: false, msg: t('*Required') });
		setIsemptyphonepassword({ error: false, msg: t('*Required') });
		setIsemptyphonecode({ error: false, msg: t('*Required') });
	};

	const resetSignupErrors = () => {
		setIsemptyfn({ error: false, msg: t('*Required') });
		setIsemptymn({ error: false, msg: t('*Required') });
		setIsemptyln({ error: false, msg: t('*Required') });
		setIsemptye({ error: false, msg: t('*Required') });
		setIsemptypc({ error: false, msg: t('*Required') });
		setIsemptypn({ error: false, msg: t('*Required') });
		setIsemptypw({ error: false, msg: t('*Required') });
		setIsemptycp({ error: false, msg: t('*Required') });
	};

	const clearSignupFields = () => {
		if (fnameRef.current) fnameRef.current.value = '';
		if (mnameRef.current) mnameRef.current.value = '';
		if (lnameRef.current) lnameRef.current.value = '';
		if (emailRef.current) emailRef.current.value = '';
		if (passwordRef.current) passwordRef.current.value = '';
		if (conpasswordRef.current) conpasswordRef.current.value = '';
		if (phoneRef.current) phoneRef.current.value = '';

		setSignupPhoneValue(defaultCountryCode ? `+${String(defaultCountryCode)}` : '');
		setppcode({});
	};

	const clearLoginFields = () => {
		if (lemailRef.current) lemailRef.current.value = '';
		if (lpasswordRef.current) lpasswordRef.current.value = '';
		if (lphoneRef.current) lphoneRef.current.value = '';
		if (lphonePasswordRef.current) lphonePasswordRef.current.value = '';
		setLoginPhoneValue(defaultCountryCode ? `+${String(defaultCountryCode)}` : '');
		setLppcode({});
	};

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 400);
		};

		window.addEventListener('resize', handleResize);
		handleResize();

		return () => window.removeEventListener('resize', handleResize);
	}, []);
	useEffect(() => {
		if (location.state?.activeTab) {
			setActiveLoginTab(location.state.activeTab);
		}
	}, [location.state?.activeTab]);

	const changeLanguage = (lang) => {
		document.querySelectorAll(u_class + ' .gt_switcher div.gt_selected').forEach(function (e) {
			e.addEventListener('click', function (evt) {
				evt.preventDefault();
				evt.stopPropagation();
				if (gt_slider_open) gt_hide_slider();
				else gt_show_slider(e.parentNode);
			});

			e.addEventListener('pointerenter', function (evt) {
				evt.target.parentNode
					.querySelectorAll('.gt_option img:not([src])')
					.forEach(function (img) {
						img.setAttribute('src', img.getAttribute('data-gt-lazy-src'));
					});
			});

			e.dispatchEvent(new PointerEvent('pointerenter', { bubbles: true, cancelable: true }));
		});

		const langLink = document.querySelector(`.gt_option a[data-gt-lang="${lang}"]`);

		if (langLink) {
			langLink.click();
			i18n.changeLanguage(lang);

			showNotification(
				<span className='d-flex align-items-center'>
					<Icon icon={getLangWithKey(lang)?.icon} size='lg' className='me-1' />
					<span>{`Language changed to ${getLangWithKey(lang)?.text}`}</span>
				</span>,
			);
		}
	};

	const handleCountrySelect = (selectedCountry) => {
		setIsemptypc({ error: false, msg: t('*Required') });
		setppcode((pre) => {
			const temp = { ...pre };
			temp['phonecRef'] = `${selectedCountry.phoneCode}`.substring(1);
			return temp;
		});
	};

	const handleLoginCountrySelect = (selectedCountry) => {
		setIsemptyphonecode({ error: false, msg: t('*Required') });
		setLppcode((pre) => {
			const temp = { ...pre };
			temp['phonecRef'] = `${selectedCountry.phoneCode}`.substring(1);
			return temp;
		});
	};

	useEffect(() => {
		const currentLang = getLangWithKey(i18n.language);

		if (!currentLang) {
			const langLink = document.querySelector(`.gt_option a[data-gt-lang="${LANG.EN.lng}"]`);

			if (langLink) {
				langLink.click();
				i18n.changeLanguage(LANG.EN.lng);
			}
		} else {
			const langLink = document.querySelector(
				`.gt_option a[data-gt-lang="${currentLang.lng}"]`,
			);

			if (langLink) {
				langLink.click();
				i18n.changeLanguage(currentLang.lng);
			}
		}

		const countryList = loginSettingsLang?.country_list;
		const phoneCodes = loginSettingsLang?.country_phone_code;
		// country_list can be null/undefined depending on backend settings; avoid Object.entries crash
		if (countryList && typeof countryList === 'object') {
			const temp = Object.entries(countryList).map(([code, name]) => ({
				code,
				name,
				phoneCode: `+${phoneCodes?.[code] ?? ''}`,
			}));
			setTableOption(temp);
		}
	}, [loginSettingsLang]);

	useEffect(() => {
		const storedTheme = localStorage.getItem('theme');
		if (storedTheme !== null) {
			setDarkModeStatus(storedTheme === 'dark');
		}
	}, [setDarkModeStatus]);

	useEffect(() => {
		localStorage.setItem('theme', darkModeStatus ? 'dark' : 'light');
		document.documentElement.setAttribute('theme', darkModeStatus ? 'dark' : 'light');
	}, [darkModeStatus]);

	async function signUpp() {
		if (isLoading) return;
		let cansendapi = true;
		const charRegex = /^[a-zA-Z\s]+$/i;
		const numberRegex = /^[0-9]+$/;
		const emailRegex = /^(?!\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/;
		const minLengthRegex =
			/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[.!@#$%^&*])[A-Za-z\d.!@#$%^&*]{8,}$/;
		const rawRequired = Array.isArray(requiredFields) ? requiredFields : [];
		let normalizedRequiredFields = rawRequired
			.map((f) =>
				String(f || '')
					.trim()
					.toLowerCase(),
			)
			.filter(Boolean);
		if (normalizedRequiredFields.length === 0) {
			normalizedRequiredFields = [
				'first_name',
				'last_name',
				'phone_code',
				'phone',
				'password',
				'confirm_password',
			];
		}
		const isRequired = (/** @type {string[]} */ ...aliases) =>
			aliases.some((a) => normalizedRequiredFields.includes(a));
		const derivedDialCode = (() => {
			if (ppcode.phonecRef) return String(ppcode.phonecRef);
			const m = signupPhoneValue?.match(/^\+(\d{1,4})/);
			return m ? m[1] : '';
		})();
		const derivedLocalNumber = (() => {
			if (!signupPhoneValue) return '';
			if (derivedDialCode) {
				return signupPhoneValue.replace(`+${derivedDialCode}`, '').replace(/\s/g, '');
			}
			return signupPhoneValue.replace(/\D/g, '');
		})();

		if (isRequired('first_name')) {
			if (!fnameRef.current.value) {
				setIsemptyfn({ error: true, msg: t('*Required') });
				cansendapi = false;
			} else if (charRegex.test(`${fnameRef.current.value}`.trim())) {
				setIsemptyfn({ error: false });
			} else {
				setIsemptyfn({ error: true, msg: `${t('Only characters accepted')}.` });
				cansendapi = false;
			}
		} else {
			if (fnameRef.current.value && !charRegex.test(`${fnameRef.current.value}`.trim())) {
				setIsemptyfn({ error: true, msg: `${t('Only characters accepted')}.` });
				cansendapi = false;
			} else {
				setIsemptyfn({ error: false });
			}
		}

		if (mnameRef.current.value.length) {
			if (charRegex.test(`${mnameRef.current.value}`.trim())) {
				setIsemptymn({ error: false });
			} else {
				setIsemptymn({ error: true, msg: `${t('Only characters accepted')}.` });
				cansendapi = false;
			}
		}

		if (isRequired('last_name')) {
			if (!lnameRef.current.value) {
				setIsemptyln({ error: true, msg: t('*Required') });
				cansendapi = false;
			} else if (charRegex.test(`${lnameRef.current.value}`.trim())) {
				setIsemptyln({ error: false });
			} else {
				setIsemptyln({ error: true, msg: `${t('Only characters accepted')}.` });
				cansendapi = false;
			}
		} else {
			if (lnameRef.current.value && !charRegex.test(`${lnameRef.current.value}`.trim())) {
				setIsemptyln({ error: true, msg: `${t('Only characters accepted')}.` });
				cansendapi = false;
			} else {
				setIsemptyln({ error: false });
			}
		}

		if (isRequired('email')) {
			if (!emailRef.current.value) {
				setIsemptye({ error: true, msg: t('*Required') });
				cansendapi = false;
			} else if (emailRegex.test(`${emailRef.current.value}`.trim())) {
				setIsemptye({ error: false });
			} else {
				setIsemptye({
					error: true,
					msg: `${t('Enter an email address with a valid format.')}.`,
				});
				cansendapi = false;
			}
		} else {
			if (emailRef.current.value && !emailRegex.test(`${emailRef.current.value}`.trim())) {
				setIsemptye({
					error: true,
					msg: `${t('Enter an email address with a valid format.')}.`,
				});
				cansendapi = false;
			} else {
				setIsemptye({ error: false });
			}
		}

		if (isRequired('phone_code')) {
			if (!derivedDialCode) {
				setIsemptypc({ error: true, msg: t('*Required') });
				cansendapi = false;
			} else {
				setIsemptypc({ error: false });
			}
		} else {
			setIsemptypc({ error: false });
		}

		if (isRequired('phone')) {
			if (!derivedLocalNumber) {
				setIsemptypn({ error: true, msg: t('*Required') });
				cansendapi = false;
			} else if (numberRegex.test(`${derivedLocalNumber}`.trim())) {
				setIsemptypn({ error: false });
			} else {
				setIsemptypn({ error: true, msg: t('Only integers accepted') });
				cansendapi = false;
			}
		} else {
			if (derivedLocalNumber && !numberRegex.test(`${derivedLocalNumber}`.trim())) {
				setIsemptypn({ error: true, msg: t('Only integers accepted') });
				cansendapi = false;
			} else {
				setIsemptypn({ error: false });
			}
		}

		if (isRequired('password')) {
			if (!passwordRef.current.value) {
				setIsemptypw({ error: true, msg: t('*Required') });
				cansendapi = false;
			} else if (minLengthRegex.test(`${passwordRef.current.value}`.trim())) {
				setIsemptypw({ error: false });
			} else {
				setIsemptypw({
					error: true,
					msg: t(
						'Must contain at least 8 characters,including UPPER/lowercase,symbles and numbers',
					),
				});
				cansendapi = false;
			}
		} else {
			if (
				passwordRef.current.value &&
				!minLengthRegex.test(`${passwordRef.current.value}`.trim())
			) {
				setIsemptypw({
					error: true,
					msg: t(
						'Must contain at least 8 characters,including UPPER/lowercase,symbles and numbers',
					),
				});
				cansendapi = false;
			} else {
				setIsemptypw({ error: false });
			}
		}

		if (isRequired('confirm_password')) {
			if (!conpasswordRef.current.value) {
				setIsemptycp({ error: true, msg: t('*Required') });
				cansendapi = false;
			} else if (minLengthRegex.test(`${conpasswordRef.current.value}`.trim())) {
				if (
					passwordRef.current.value &&
					conpasswordRef.current.value &&
					passwordRef.current.value !== conpasswordRef.current.value
				) {
					setIsemptycp({ error: true, msg: t('Passwords do not match') });
					cansendapi = false;
				} else {
					setIsemptycp({ error: false });
				}
			} else {
				setIsemptycp({
					error: true,
					msg: t(
						'Must contain at least 8 characters,including UPPER/lowercase,symbles and numbers',
					),
				});
				cansendapi = false;
			}
		} else {
			if (
				conpasswordRef.current.value &&
				!minLengthRegex.test(`${conpasswordRef.current.value}`.trim())
			) {
				setIsemptycp({
					error: true,
					msg: t(
						'Must contain at least 8 characters,including UPPER/lowercase,symbles and numbers',
					),
				});
				cansendapi = false;
			} else {
				setIsemptycp({ error: false });
			}
		}

		if (String(derivedDialCode) === '94') {
			let local = String(derivedLocalNumber || '').replace(/\s/g, '');
			if (local.startsWith('+94')) local = local.slice(3);
			if (local.startsWith('94')) local = local.slice(2);

			let isValid = false;
			let normalizedLocal = local;

			if (local.length === 10 && local.startsWith('0')) {
				isValid = true;
				normalizedLocal = local.slice(1);
			} else if (local.length === 9 && !local.startsWith('0')) {
				isValid = true;
			}

			if (!isValid) {
				setIsemptypn({
					error: true,
					msg: t('*Phone Number should be 9 digits (or 10 if starting with 0)'),
				});
				cansendapi = false;
			} else {
				setIsemptypn({ error: false, msg: '' });

				const desired = `+94${normalizedLocal}`;
				if ((signupPhoneValue || '').replace(/\s/g, '') !== desired) {
					setSignupPhoneValue(desired);
				}
				setppcode((prev) => ({ ...prev, phonecRef: '94' }));
				if (phoneRef?.current) {
					phoneRef.current.value = normalizedLocal;
				}
			}
		} else {
			const local = String(derivedLocalNumber || '').replace(/\s/g, '');
			if (derivedDialCode && local) {
				const digitsOnly = /^\d+$/.test(local);
				const minLen = 6;
				const maxLen = 12;
				if (!digitsOnly) {
					setIsemptypn({ error: true, msg: t('Only integers accepted') });
					cansendapi = false;
				} else if (local.length < minLen || local.length > maxLen) {
					setIsemptypn({
						error: true,
						msg: t('Phone number length must be between {{min}} and {{max}} digits', {
							min: minLen,
							max: maxLen,
						}),
					});
					cansendapi = false;
				} else {
					setIsemptypn({ error: false, msg: '' });
				}
			}
		}

		if (cansendapi) {
			let updatedMobileNumber = String(derivedLocalNumber || '').replace(/\D/g, '');

			if (derivedDialCode && updatedMobileNumber.startsWith(String(derivedDialCode))) {
				updatedMobileNumber = updatedMobileNumber.slice(String(derivedDialCode).length);
			}

			if (
				String(derivedDialCode) === '94' &&
				updatedMobileNumber.length > 9 &&
				updatedMobileNumber.startsWith('0')
			) {
				updatedMobileNumber = updatedMobileNumber.slice(1);
			}

			setIsLoading(true);
			const item = {
				action: 'registration',
				first_name: fnameRef.current.value,
				middle_name: mnameRef.current.value,
				last_name: lnameRef.current.value,
				email: emailRef.current.value,
				phone_code: derivedDialCode,
				phone: updatedMobileNumber,
				password: passwordRef.current.value,
				confirmed: conpasswordRef.current.value,
			};
			const weblink = getConfig().REACT_APP_WEB_LINK;
			const slug = getClientSlug();
			const payload = slug ? { ...item, slug: slug } : item;

			try {
				const res = await fetch(weblink, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
					},
					body: JSON.stringify(payload),
				});
				const result = await res.json();
				if (result.type === 'Success' || result.type === 'success') {
					startmodel();
					setMsgLoadalert(result.message);
					setMsgtypeLoadalert(result.type);
					setTimeout(() => {
						navigate('/');
					}, 2000);
				} else {
					startmodel();
					setMsgLoadalert(result.message);
					setMsgtypeLoadalert(result.type);
				}
			} catch (error) {
				console.error('Error fetching data:', error);
			} finally {
				setIsLoading(false);
			}
		}
	}

	const { token, setToken } = useContext(userAuth);

	const [isemptym, setIsemptym] = useState({ error: false, msg: t('*Required') });
	const [isemptyp, setIsemptyp] = useState({ error: false, msg: t('*Required') });
	const [isemptyphone, setIsemptyphone] = useState({ error: false, msg: t('*Required') });
	const [isemptyphonepassword, setIsemptyphonepassword] = useState({
		error: false,
		msg: t('*Required'),
	});
	const [isemptyphonecode, setIsemptyphonecode] = useState({ error: false, msg: t('*Required') });
	const [activeLoginTab, setActiveLoginTab] = useState('email');
	const [tabDirection, setTabDirection] = useState('');
	const [isAnimating, setIsAnimating] = useState(false);
	const [lppcode, setLppcode] = useState({});
	const [loginPhoneValue, setLoginPhoneValue] = useState('');

	const [isLoading, setIsLoading] = useState(false);
	const loginWithOtpEnabled = !!loginSettings?.permission_settings?.login?.login_with_otp;

	const registrationAllowSetting = loginSettings?.permission_settings?.registration?.allow;
	const registrationAllow = loginSettingsLoading
		? true
		: typeof registrationAllowSetting === 'boolean'
		? registrationAllowSetting
		: true;
	const effectiveIsNewUser = registrationAllow && isNewUser;
	const requiredFields = loginSettings?.permission_settings?.registration?.required_fields || [];
	const rawDefaultCountryCode =
		loginSettings?.permission_settings?.default_country_code ??
		loginSettings?.permission_settings?.registration?.default_country_code;
	const defaultCountryCode = String(rawDefaultCountryCode || '').trim() || '94';

	useEffect(() => {
		if (defaultCountryCode && !ppcode.phonecRef) {
			setppcode((prev) => ({ ...prev, phonecRef: String(defaultCountryCode) }));
			setSignupPhoneValue(`+${String(defaultCountryCode)}`);
			setLoginPhoneValue(`+${String(defaultCountryCode)}`);
		}
	}, [defaultCountryCode, ppcode.phonecRef]);

	const switchTab = (newTab) => {
		if (newTab === activeLoginTab || isAnimating) return;

		setIsAnimating(true);
		const direction = newTab === 'email' ? 'right' : 'left';
		setTabDirection(direction);
		setTimeout(() => {
			setActiveLoginTab(newTab);
			setLppcode({});
			setIsemptyphonecode({ error: false });
			setIsemptyphone({ error: false });
			setIsemptyphonepassword({ error: false });
			setIsemptym({ error: false });
			setIsemptyp({ error: false });
			setTimeout(() => {
				setIsAnimating(false);
				setTabDirection('');
			}, 300);
		}, 150);
	};

	const login = async () => {
		if (isLoading) return;
		let cansendapi = true;
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const phoneRegex = /^\+?[0-9]+$/;

		const inputValue = lemailRef.current.value.trim();
		let detectedLoginType = 'email';
		if (phoneRegex.test(inputValue)) {
			detectedLoginType = 'phone';
		} else if (emailRegex.test(inputValue)) {
			detectedLoginType = 'email';
		}

		setLoginType(detectedLoginType);

		if (!inputValue) {
			setIsemptym({ error: true, msg: t('*Required') });
			cansendapi = false;
		} else if (detectedLoginType === 'email' && emailRegex.test(inputValue)) {
			setIsemptym({ error: false });
		} else if (detectedLoginType === 'phone' && phoneRegex.test(inputValue)) {
			setIsemptym({ error: false });
		} else {
			setIsemptym({
				error: true,
				msg: t('Enter a valid email address or phone number'),
			});
			cansendapi = false;
		}

		if (!lpasswordRef.current.value) {
			setIsemptyp({ error: true, msg: t('*Required') });
			cansendapi = false;
		} else {
			setIsemptyp({ error: false });
		}

		if (cansendapi) {
			setIsLoading(true);
			const item = {
				action: 'login',
				email: inputValue,
				password: lpasswordRef.current.value,
			};

			const weblink = getConfig().REACT_APP_WEB_LINK;
			const slug = getClientSlug();
			const payload = slug ? { ...item, slug: slug } : item;

			try {
				const res = await fetch(weblink, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
					},
					body: JSON.stringify(payload),
				});
				const result = await res.json();

				if (result.type === 'Success' || result.type === 'success') {
					if (result.data.userrole === 'cus') {
						setToken(result.data);
						localStorage.setItem('user-auth', JSON.stringify(result.data));
						fetchBranches(result.data);

						if (cameForLogin && serviceId) {
							goToCalendar(serviceId);
						} else {
							navigate('/services');
						}
					} else {
						startmodel();
						setMsgLoadalert(result.message);
						setMsgtypeLoadalert(result.type);
					}
				} else {
					startmodel();
					setMsgLoadalert(result.message);
					setMsgtypeLoadalert(result.type);
				}
			} catch (error) {
			} finally {
				setIsLoading(false);
			}
		}
	};

	const requestOTP = async () => {
		if (isLoading) return;

		// Mirror signup validation: derive dial & local number from state or input
		let cansendapi = true;
		const numberRegex = /^[0-9]+$/;
		const derivedDialCode = (() => {
			if (lppcode && lppcode.phonecRef) return String(lppcode.phonecRef);
			const m = (loginPhoneValue || '').match(/^\+(\d{1,4})/);
			return m ? m[1] : '';
		})();
		const derivedLocalNumber = (() => {
			if (!loginPhoneValue) return '';
			if (derivedDialCode) {
				return loginPhoneValue.replace(`+${derivedDialCode}`, '').replace(/\s/g, '');
			}
			return (loginPhoneValue || '').replace(/\D/g, '');
		})();

		// Raw full phone value without spaces (used for API payload)
		const rawFull = (loginPhoneValue || '').replace(/\s/g, '');

		// Basic presence/format checks
		if (!derivedLocalNumber) {
			setIsemptyphone({ error: true, msg: t('*Required') });
			cansendapi = false;
		} else if (numberRegex.test(`${derivedLocalNumber}`.trim())) {
			setIsemptyphone({ error: false });
		} else {
			setIsemptyphone({ error: true, msg: t('Only integers accepted') });
			cansendapi = false;
		}

		// Country-specific / length checks (Sri Lanka special-case and general 6-12 bounds)
		if (cansendapi) {
			if (String(derivedDialCode) === '94') {
				let local = String(derivedLocalNumber || '').replace(/\s/g, '');
				if (local.startsWith('+94')) local = local.slice(3);
				if (local.startsWith('94')) local = local.slice(2);

				let isValid = false;
				let normalizedLocal = local;

				if (local.length === 10 && local.startsWith('0')) {
					isValid = true;
					normalizedLocal = local.slice(1);
				} else if (local.length === 9 && !local.startsWith('0')) {
					isValid = true;
				}

				if (!isValid) {
					setIsemptyphone({
						error: true,
						msg: t('*Phone Number should be 9 digits (or 10 if starting with 0)'),
					});
					cansendapi = false;
				} else {
					setIsemptyphone({ error: false, msg: '' });
					// normalize loginPhoneValue to +94<normalizedLocal> to match signup behavior
					const desired = `+94${normalizedLocal}`;
					if ((loginPhoneValue || '').replace(/\s/g, '') !== desired) {
						setLoginPhoneValue(desired);
					}
					setppcode((prev) => ({ ...(prev || {}), phonecRef: '94' }));
					if (lphoneRef?.current) {
						lphoneRef.current.value = normalizedLocal;
					}
				}
			} else {
				const local = String(derivedLocalNumber || '').replace(/\s/g, '');
				if (derivedDialCode && local) {
					const digitsOnly = /^\d+$/.test(local);
					const minLen = 6;
					const maxLen = 12;
					if (!digitsOnly) {
						setIsemptyphone({ error: true, msg: t('Only integers accepted') });
						cansendapi = false;
					} else if (local.length < minLen || local.length > maxLen) {
						setIsemptyphone({
							error: true,
							msg: t(
								'Phone number length must be between {{min}} and {{max}} digits',
								{ min: minLen, max: maxLen },
							),
						});
						cansendapi = false;
					} else {
						setIsemptyphone({ error: false });
					}
				}
			}
		}

		if (cansendapi) {
			setIsLoading(true);
			const item = {
				action: 'request_otp',
				phone_number: rawFull,
			};

			try {
				const result = await apiServicewt(item, navigate);

				if (result.type === 'Success' || result.type === 'success') {
					setTimeout(() => {
						navigate('/landing/verefiyotp', {
							state: {
								phone_number: lphoneRef.current.value,
								...result,
							},
						});
					}, 2000);
				} else {
					startmodel();
					setMsgLoadalert(result.message);
					setMsgtypeLoadalert(result.type);
				}
			} catch (error) {
				console.error('Error requesting OTP:', error);
			} finally {
				setIsLoading(false);
			}
		}
	};

	const goToCalendar = (id) => {
		setServiceidcont(id);
		navigate(`../${dashboardMenu.Celender.path}`);
	};

	const fetchBranches = async (tokenData) => {
		const item = { action: 'get_branches_list' };

		try {
			const flechdata = await apiService(item, tokenData, setToken, navigate);

			if (Array.isArray(flechdata.data)) {
				const temp = flechdata.data.map((ele) => ({
					value: ele.branch_id,
					text: ele.branch_name,
				}));

				const defaultBranch = flechdata.data.find((branch) => branch.is_default);
				localStorage.setItem('branches-list', JSON.stringify(temp));
				localStorage.setItem(
					'selectedBranch',
					defaultBranch ? defaultBranch.branch_id : temp[0].value,
				);

				const selectedBranchName = temp?.find((branch) =>
					String(branch.value) === defaultBranch
						? defaultBranch.branch_id
						: temp[0].value,
				)?.text;

				window.dispatchEvent(
					new CustomEvent('branchChanged', {
						detail: { name: selectedBranchName },
					}),
				);
			} else {
				localStorage.removeItem('branches-list');
			}
		} catch (error) {
			localStorage.removeItem('branches-list');
			console.error('Error fetching branches:', error);
		}
	};

	const closemodel = () => {
		setLoadalert(false);
	};
	const startmodel = () => {
		setLoadalert(true);
	};
	const closemodel2 = () => {
		setIsempty2(false);
	};
	const startmodel2 = () => {
		setIsempty2(true);
	};
	useEffect(() => {
		setLoaderv(true);
		const authtimer = setTimeout(() => {
			if (token.email) {
				navigate('/services');
			} else {
				setLoaderv(false);
			}
		}, 300);
		return () => {
			clearTimeout(authtimer);
		};
	}, [token.email]);
	if (loaderv) {
		return <Loader />;
	}

	return (
		<>
			<Ralert
				msgloadalert={msgloadalert}
				msgtypeloadalert={msgtypeloadalert}
				loadalert={loadalert}
				closemodel={closemodel}
			/>
			<Rodal visible={isempty2} onClose={closemodel2}>
				<div
					style={{
						height: '244px',
						width: '404px',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-between',
						backgroundColor: darkModeStatus ? '#141414' : '#f5f5f7',
						border: darkModeStatus ? '4px solid #f5f5f7' : '4px solid #141414',
						borderRadius: '8px',
					}}>
					<div
						style={{
							display: 'flex',
							paddingTop: '30px',
							width: '100%',
							alignItems: 'center',
							flexDirection: 'column',
							justifyContent: 'space-between',
						}}>
						<Icon icon='ErrorOutline' color='danger' size='6x' />{' '}
						{/* Replace with your desired icon */}
						<span
							style={{
								color: darkModeStatus ? '#e5e5e5' : '#141414',
								fontSize: '15px',
								display: 'flex',
								width: '100%',
								alignItems: 'center',
								flexDirection: 'column',
								justifyContent: 'space-between',
								fontWeight: 'bold',
							}}>
							{t('Login Failed!')}
						</span>
					</div>
					<div
						style={{
							width: '100%',
							display: 'flex',
							justifyContent: 'center',
							paddingBottom: '20px',
						}}>
						{`${t('Please Recheck the Username and Password')}.`}
					</div>
					<div
						style={{
							width: '100%',
							display: 'flex',
							justifyContent: 'center',
							paddingBottom: '20px',
						}}>
						<Button
							style={{
								width: '100px',
								height: '40px',
								borderRadius: '7px',
								fontSize: '16px',
								backgroundColor: '#e6e6e6',
								color: '#000000',
								border: '3px solid #cecfd3',
								paddingBottom: '10px',
							}}
							color='danger'
							isLight
							onClick={closemodel2}>
							{t('Close')}
						</Button>
					</div>
				</div>
			</Rodal>

			<div className='bg-img' style={{ backgroundImage: `url(${loginBg})` }}>
				<PageWrapper title={effectiveIsNewUser ? 'Sign Up' : 'Login'}>
					<div
						className='g-3'
						style={{
							position: 'absolute',
							top: 8,
							right: 8,
							display: 'flex',
							justifyContent: 'flex-end',
						}}>
						{/* <HeaderRight> */}
						<div className='col-md-auto'>
							<div className='row g-3'>
								<div className='col-auto'>
									<Popovers trigger='hover' desc='Dark / Light mode'>
										<Button
											{...styledBtn}
											icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
											onClick={() => setDarkModeStatus((prev) => !prev)}
											aria-label='Toggle fullscreen'
											data-tour='dark-mode'
										/>
									</Popovers>
								</div>
								{isLangSwitchEnabled && (
									<div className='col-auto'>
										<Dropdown>
											<DropdownToggle hasIcon={false}>
												<Button
													{...styledBtn}
													icon={getLangWithKey(i18n.language)?.icon}
													aria-label='Change language'
													data-tour='lang-selector'
												/>
											</DropdownToggle>
											<DropdownMenu
												isAlignmentEnd
												data-tour='lang-selector-menu'>
												{Object.keys(LANG).map((key) => (
													<DropdownItem key={LANG[key].lng}>
														<Button
															icon={LANG[key].icon}
															onClick={() =>
																changeLanguage(LANG[key].lng)
															}>
															{LANG[key].text}
														</Button>
													</DropdownItem>
												))}
											</DropdownMenu>
										</Dropdown>
									</div>
								)}
							</div>
						</div>
						{/* </HeaderRight> */}
					</div>
					<Page
						className='p-0'
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							minHeight: '100vh',
						}}>
						<Card
							className={`shadow-3d-dark w-100 ${isMobile ? 'mx-3' : ''}`}
							data-tour='login-page'
							style={{
								maxWidth: effectiveIsNewUser ? '680px' : '360px',
								maxHeight: '760px',
							}}>
							<CardBody
								style={{
									justifyContent: 'center',
								}}>
								<div className='text-center my-3'>
									<a
										href='http://schedulerdesk.com/'
										className={classNames(
											'text-decoration-none fw-bold display-2',
											{
												'text-dark': !darkModeStatus,
												'text-light': darkModeStatus,
											},
										)}>
										<Logo width={200} />
									</a>
								</div>
								{registrationAllow && (
									<div
										className={classNames('rounded-3', {
											'bg-l10-dark': !darkModeStatus,
											'bg-lo10-dark': darkModeStatus,
										})}>
										<div className='row row-cols-2 g-3 pb-3 px-3 mb-1'>
											<div className='col'>
												<Button
													color={darkModeStatus ? 'light' : 'dark'}
													isLight={!!effectiveIsNewUser}
													className='rounded-1 w-100'
													size='lg'
													onClick={() => {
														setUsernameInput(false);
														resetLoginErrors();
														resetSignupErrors();
														clearSignupFields();
														setIsNewUser(false);
													}}>
													{t('Login')}
												</Button>
											</div>
											<div className='col'>
												<Button
													color={darkModeStatus ? 'light' : 'dark'}
													isLight={!effectiveIsNewUser}
													className='rounded-1 w-100'
													size='lg'
													onClick={() => {
														setUsernameInput(false);
														resetLoginErrors();
														resetSignupErrors();
														clearLoginFields();
														setIsNewUser(true);
													}}>
													{t('Sign Up')}
												</Button>
											</div>
										</div>
									</div>
								)}

								<LoginHeader isNewUser={effectiveIsNewUser} />

								<form className='row g-4'>
									{effectiveIsNewUser ? (
										<div className='mt-5'>
											<div className='row g-8'>
												<div
													className={
														mobileres == 'ab' ? 'col-12' : 'col-4'
													}
													style={{
														paddingBottom:
															mobileres == 'ab' ? '20px' : '',
													}}>
													<FormGroup
														isFloating
														label={
															<span>
																{t('First Name')}{' '}
																<span
																	style={{
																		color: '#dc3545',
																		fontWeight: 'bold',
																		marginLeft: '2px',
																	}}>
																	*
																</span>
															</span>
														}>
														<Input type='text' ref={fnameRef} />
													</FormGroup>
													{isemptyfn.error && (
														<p
															style={{
																color: 'red',
																fontSize: '12px',
																marginBottom: '0px',
															}}>
															{' '}
															{isemptyfn.msg
																? isemptyfn.msg
																: t('*Required')}
														</p>
													)}
												</div>
												<div
													className={
														mobileres == 'ab' ? 'col-12' : 'col-4'
													}
													style={{
														paddingBottom:
															mobileres == 'ab' ? '20px' : '',
													}}>
													<FormGroup isFloating label={t('Middle Name')}>
														<Input type='text' ref={mnameRef} />
													</FormGroup>
													{isemptymn.error && (
														<p
															style={{
																color: 'red',
																fontSize: '12px',
																marginBottom: '0px',
															}}>
															{isemptymn.msg
																? isemptymn.msg
																: `${t(
																		'Only characters accepted',
																  )}.`}
														</p>
													)}
												</div>
												<div
													className={
														mobileres == 'ab' ? 'col-12' : 'col-4'
													}>
													<FormGroup
														isFloating
														label={
															<span>
																{t('Last Name')}{' '}
																<span
																	style={{
																		color: '#dc3545',
																		fontWeight: 'bold',
																		marginLeft: '2px',
																	}}>
																	*
																</span>
															</span>
														}>
														<Input type='text' ref={lnameRef} />
													</FormGroup>
													{isemptyln.error && (
														<p
															style={{
																color: 'red',
																fontSize: '12px',
																marginBottom: '0px',
															}}>
															{' '}
															{isemptyln.msg
																? isemptyln.msg
																: t('*Required')}
														</p>
													)}
												</div>
											</div>
											<div className='row g-8'>
												<div className='col-6 mt-4'>
													<FormGroup isFloating label={t('Email')}>
														<Input type='email' ref={emailRef} />
													</FormGroup>
													{isemptye.error && (
														<p
															style={{
																color: 'red',
																fontSize: '12px',
																marginBottom: '0px',
															}}>
															{isemptye.msg
																? isemptye.msg
																: t('*Required')}
														</p>
													)}
												</div>
												<div className='col-6 mt-4'>
													<FormGroup
														isFloating
														label={
															<span>
																{t('')}{' '}
																<span
																	style={{
																		color: '#dc3545',
																		fontWeight: 'bold',
																		marginLeft: '265px',
																	}}>
																	*
																</span>
															</span>
														}>
														<PhoneNumberInput
															value={signupPhoneValue}
															defaultCountryIso={(
																tableOption.find(
																	(opt) =>
																		opt.phoneCode ===
																		`+${defaultCountryCode}`,
																)?.code || ''
															).toLowerCase()}
															onChange={(val, country) => {
																setSignupPhoneValue(val);
																const dial =
																	country?.dialCode || '';
																setppcode((prev) => ({
																	...prev,
																	phonecRef: dial,
																}));
																if (phoneRef.current) {
																	const local = val
																		.replace(`+${dial}`, '')
																		.replace(/\s/g, '');
																	phoneRef.current.value = local;
																}
																if (!dial) {
																	setIsemptypc({
																		error: true,
																		msg: t('*Required'),
																	});
																} else {
																	setIsemptypc({
																		error: false,
																		msg: t('*Required'),
																	});
																}
																const localNow = (val || '')
																	.replace(`+${dial}`, '')
																	.replace(/\s/g, '');
																if (dial && !localNow) {
																	setIsemptypn({
																		error: true,
																		msg: t('*Required'),
																	});
																} else if (
																	localNow &&
																	!/^\d+$/.test(localNow)
																) {
																	setIsemptypn({
																		error: true,
																		msg: t(
																			'Only integers accepted',
																		),
																	});
																} else {
																	setIsemptypn({
																		error: false,
																		msg: t('*Required'),
																	});
																}
															}}
														/>
													</FormGroup>
													{(isemptypc.error || isemptypn.error) && (
														<p
															style={{
																color: 'red',
																fontSize: '12px',
																marginBottom: '0px',
															}}>
															{isemptypc.error
																? isemptypc.msg || t('*Required')
																: isemptypn.msg || t('*Required')}
														</p>
													)}
												</div>
											</div>
											<div className='row g-8'>
												<div className='col-6  mt-4'>
													<FormGroup
														isFloating
														label={
															<span>
																{t('Password')}{' '}
																<span
																	style={{
																		color: '#dc3545',
																		fontWeight: 'bold',
																		marginLeft: '2px',
																	}}>
																	*
																</span>
															</span>
														}>
														<Input
															type={
																showPassword ? 'text' : 'password'
															}
															ref={passwordRef}
														/>
														<button
															type='button'
															onClick={togglePasswordVisibility}
															style={{
																position: 'absolute',
																right: '10px',
																top: '50%',
																transform: 'translateY(-50%)',
																border: 'none',
																background: 'none',
																cursor: 'pointer',
															}}>
															{showPassword ? (
																<Icon
																	style={{
																		color: darkModeStatus
																			? '#c9c3c3'
																			: 'black',
																	}}
																	icon='Visibility'
																	size='2x'
																/>
															) : (
																<Icon
																	style={{
																		color: darkModeStatus
																			? '#c9c3c3'
																			: 'black',
																	}}
																	icon='VisibilityOff'
																	size='2x'
																/>
															)}{' '}
														</button>
													</FormGroup>
													{isemptypw.error && (
														<p
															style={{
																color: 'red',
																fontSize: '12px',
																marginBottom: '0px',
															}}>
															{' '}
															{isemptypw.msg
																? isemptypw.msg
																: t('*Required')}
														</p>
													)}
												</div>
												<div className='col-6  mt-4'>
													<FormGroup
														isFloating
														label={
															<span>
																{t('Confirm Password')}{' '}
																<span
																	style={{
																		color: '#dc3545',
																		fontWeight: 'bold',
																		marginLeft: '2px',
																	}}>
																	*
																</span>
															</span>
														}>
														<Input
															ref={conpasswordRef}
															type={
																cshowPassword ? 'text' : 'password'
															}
														/>
														<button
															type='button'
															onClick={ctogglePasswordVisibility}
															style={{
																position: 'absolute',
																right: '10px',
																top: '50%',
																transform: 'translateY(-50%)',
																border: 'none',
																background: 'none',
																cursor: 'pointer',
															}}>
															{cshowPassword ? (
																<Icon
																	style={{
																		color: darkModeStatus
																			? '#c9c3c3'
																			: 'black',
																	}}
																	icon='Visibility'
																	size='2x'
																/>
															) : (
																<Icon
																	style={{
																		color: darkModeStatus
																			? '#c9c3c3'
																			: 'black',
																	}}
																	icon='VisibilityOff'
																	size='2x'
																/>
															)}{' '}
														</button>
													</FormGroup>
													{isemptycp.error && (
														<p
															style={{
																color: 'red',
																fontSize: '12px',
																marginBottom: '0px',
															}}>
															{' '}
															{isemptycp.msg
																? isemptycp.msg
																: t('*Required')}
														</p>
													)}
												</div>
											</div>
											<div className='col-12 mt-5 mb-2'>
												<Button
													color='info'
													className='w-100 py-3'
													onClick={() => signUpp()}
													disabled={isLoading}>
													{t('Sign Up')}
												</Button>
											</div>
										</div>
									) : (
										<>
											<div className='w-100' style={{ maxWidth: '360px' }}>
												<div className='col-12'>
													{loginWithOtpEnabled && (
														<div className='d-flex justify-content-center mb-2 mt-4'>
															<ul
																className='nav nav-tabs border-0'
																style={{
																	backgroundColor: 'transparent',
																}}>
																<li className='nav-item'>
																	<button
																		type='button'
																		className={classNames(
																			'nav-link border-0 px-4 py-2 fw-semibold',
																			{
																				'active bg-primary text-white rounded-start':
																					activeLoginTab ===
																					'email',
																				'bg-light text-dark':
																					activeLoginTab !==
																						'email' &&
																					!darkModeStatus,
																				'bg-dark text-light':
																					activeLoginTab !==
																						'email' &&
																					darkModeStatus,
																			},
																		)}
																		style={{
																			borderRadius:
																				activeLoginTab ===
																				'email'
																					? '8px 0 0 8px'
																					: '8px 0 0 8px',
																			minWidth: '140px',
																			transition:
																				'all 0.3s ease',
																		}}
																		onClick={() => {
																			switchTab('email');
																		}}>
																		{t('Email')}
																	</button>
																</li>
																<li className='nav-item'>
																	<button
																		type='button'
																		className={classNames(
																			'nav-link border-0 px-4 py-2 fw-semibold',
																			{
																				'active bg-primary text-white rounded-end':
																					activeLoginTab ===
																					'phone',
																				'bg-light text-dark':
																					activeLoginTab !==
																						'phone' &&
																					!darkModeStatus,
																				'bg-dark text-light':
																					activeLoginTab !==
																						'phone' &&
																					darkModeStatus,
																			},
																		)}
																		style={{
																			borderRadius:
																				activeLoginTab ===
																				'phone'
																					? '0 8px 8px 0'
																					: '0 8px 8px 0',
																			minWidth: '140px',
																			transition:
																				'all 0.3s ease',
																		}}
																		onClick={() => {
																			switchTab('phone');
																		}}>
																		{t('Phone Number')}
																	</button>
																</li>
															</ul>
														</div>
													)}
													<div
														style={{
															position: 'relative',
															paddingTop: '20px',
															overflow: isAnimating
																? 'hidden'
																: 'visible',
															height: (() => {
																const emailBaseHeight = 190;
																const phoneBaseHeight = 80;
																const errorHeight = 15;
																const demoBannerHeight =
																	isDemoMode &&
																	activeLoginTab === 'email'
																		? 100
																		: 0;

																if (activeLoginTab === 'email') {
																	const hasEmailErrors =
																		isemptym.error ||
																		isemptyp.error;
																	return hasEmailErrors
																		? `${
																				emailBaseHeight +
																				errorHeight +
																				demoBannerHeight
																		  }px`
																		: `${
																				emailBaseHeight +
																				demoBannerHeight
																		  }px`;
																} else {
																	const hasPhoneErrors =
																		isemptyphone.error;
																	return hasPhoneErrors
																		? `${
																				phoneBaseHeight +
																				errorHeight
																		  }px`
																		: `${phoneBaseHeight}px`;
																}
															})(),
															minHeight: isAnimating
																? '80px'
																: 'auto',
															transition:
																'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
															willChange: 'height',
														}}>
														{activeLoginTab === 'email' && (
															<>
																{isDemoMode && (
																	<div
																		className={classNames(
																			'mb-4 p-3',
																			{
																				'bg-light border border-success':
																					!darkModeStatus,
																				'bg-lo10-dark border border-success':
																					darkModeStatus,
																			},
																		)}
																		style={{
																			borderLeftWidth: '4px',
																			borderRadius: '6px',
																			fontSize: '12px',
																			marginBottom: isDemoMode
																				? '12px'
																				: '0',
																		}}>
																		<div
																			className='d-flex align-items-center mb-2'
																			style={{
																				fontWeight: 600,
																			}}>
																			<Icon
																				icon='Info'
																				size='lg'
																				className='me-2'
																				color='success'
																			/>
																			Demo Login
																		</div>

																		<div
																			style={{
																				lineHeight: 1.6,
																				opacity: 0.85,
																			}}>
																			<div>
																				<strong>
																					Email:
																				</strong>{' '}
																				employee@interflext.com
																			</div>
																			<div>
																				<strong>
																					Password:
																				</strong>{' '}
																				Demo123@
																			</div>
																		</div>
																	</div>
																)}
																<div
																	style={{
																		animation:
																			tabDirection === 'right'
																				? 'slideInFromLeft 0.3s ease-out'
																				: tabDirection ===
																				  'left'
																				? 'slideOutToLeft 0.15s ease-in'
																				: 'none',
																		...(tabDirection ===
																			'left' && {
																			position: 'absolute',
																			opacity: 0,
																		}),
																	}}>
																	<FormGroup
																		isFloating
																		label={t(
																			'Your Email or Phone Number',
																		)}>
																		<Input
																			autoComplete='email'
																			ref={lemailRef}
																			onChange={() =>
																				setIsemptym({
																					error: false,
																					msg: t(
																						'*Required',
																					),
																				})
																			}
																		/>
																	</FormGroup>
																	{isemptym.error ? (
																		<p
																			style={{
																				color: 'red',
																				fontSize: '12px',
																			}}>
																			{' '}
																			{isemptym.msg
																				? isemptym.msg
																				: t('*Required')}
																		</p>
																	) : (
																		<br />
																	)}

																	<FormGroup
																		isFloating
																		label={t('Password')}>
																		<Input
																			type={
																				lshowPassword
																					? 'text'
																					: 'password'
																			}
																			ref={lpasswordRef}
																		/>
																		<button
																			type='button'
																			onClick={
																				ltogglePasswordVisibility
																			}
																			style={{
																				position:
																					'absolute',
																				right: '10px',
																				top: '50%',
																				transform:
																					'translateY(-50%)',
																				border: 'none',
																				background: 'none',
																				cursor: 'pointer',
																			}}>
																			{lshowPassword ? (
																				<Icon
																					style={{
																						color: darkModeStatus
																							? '#c9c3c3'
																							: 'black',
																					}}
																					icon='Visibility'
																					size='2x'
																				/>
																			) : (
																				<Icon
																					style={{
																						color: darkModeStatus
																							? '#c9c3c3'
																							: 'black',
																					}}
																					icon='VisibilityOff'
																					size='2x'
																				/>
																			)}{' '}
																		</button>
																	</FormGroup>
																	{isemptyp.error ? (
																		<p
																			style={{
																				color: 'red',
																				fontSize: '12px',
																			}}>
																			{' '}
																			{isemptyp.msg
																				? isemptyp.msg
																				: t('*Required')}
																		</p>
																	) : (
																		<br />
																	)}

																	<a
																		href='landing/forgot?login_type=email'
																		className={classNames(
																			'text-decoration-none me-3 d-block',
																			{
																				'link-light':
																					darkModeStatus,
																				white: !darkModeStatus,
																			},
																		)}
																		style={{
																			marginLeft: '8px',
																			fontSize: 13,
																			marginBottom: '20px',
																		}}>
																		{t('Forgotten password?')}
																	</a>
																</div>
															</>
														)}

														{/* Phone Login Form */}
														{loginWithOtpEnabled &&
															activeLoginTab === 'phone' && (
																<div
																	style={{
																		animation:
																			tabDirection === 'left'
																				? 'slideInFromRight 0.3s ease-out'
																				: tabDirection ===
																				  'right'
																				? 'slideOutToRight 0.15s ease-in'
																				: 'none',
																		...(tabDirection ===
																			'right' && {
																			position: 'absolute',
																			opacity: 0,
																		}),
																	}}>
																	<FormGroup isFloating>
																		<PhoneNumberInput
																			value={loginPhoneValue}
																			defaultCountryIso={(
																				tableOption.find(
																					(opt) =>
																						opt.phoneCode ===
																						`+${defaultCountryCode}`,
																				)?.code || ''
																			).toLowerCase()}
																			onChange={(
																				val,
																				country,
																			) => {
																				setLoginPhoneValue(
																					val,
																				);
																				const dial =
																					country?.dialCode ||
																					'';
																				setLppcode(
																					(prev) => ({
																						...(prev ||
																							{}),
																						phonecRef:
																							dial,
																					}),
																				);

																				if (
																					lphoneRef.current
																				) {
																					const local =
																						val
																							.replace(
																								`+${dial}`,
																								'',
																							)
																							.replace(
																								/\s/g,
																								'',
																							);
																					lphoneRef.current.value =
																						local ||
																						val;
																				}
																				const localNow = (
																					val || ''
																				)
																					.replace(
																						`+${dial}`,
																						'',
																					)
																					.replace(
																						/\s/g,
																						'',
																					);
																				if (!dial) {
																					setIsemptyphonecode(
																						{
																							error: true,
																							msg: t(
																								'*Required',
																							),
																						},
																					);
																				} else {
																					setIsemptyphonecode(
																						{
																							error: false,
																							msg: t(
																								'*Required',
																							),
																						},
																					);
																				}
																				if (
																					dial &&
																					!localNow
																				) {
																					setIsemptyphone(
																						{
																							error: true,
																							msg: t(
																								'*Required',
																							),
																						},
																					);
																				} else if (
																					localNow &&
																					!/^\d+$/.test(
																						localNow,
																					)
																				) {
																					setIsemptyphone(
																						{
																							error: true,
																							msg: t(
																								'Only integers accepted',
																							),
																						},
																					);
																				} else {
																					setIsemptyphone(
																						{
																							error: false,
																							msg: t(
																								'*Required',
																							),
																						},
																					);
																				}
																			}}
																		/>
																	</FormGroup>
																	{isemptyphone.error ? (
																		<p
																			style={{
																				color: 'red',
																				fontSize: '12px',
																			}}>
																			{isemptyphone.msg
																				? isemptyphone.msg
																				: t('*Required')}
																		</p>
																	) : (
																		<br />
																	)}
																</div>
															)}
													</div>
												</div>

												<div className='col-12 mb-3 mt-4'>
													<Button
														color='info'
														className='w-100 py-3 shadow-sm'
														style={{
															borderRadius: '8px',
															fontWeight: 'bold',
														}}
														onClick={
															loginWithOtpEnabled
																? activeLoginTab === 'email'
																	? login
																	: requestOTP
																: login
														}
														disabled={isLoading}>
														{loginWithOtpEnabled
															? activeLoginTab === 'email'
																? t('Login')
																: t('Login with OTP')
															: t('Login')}
													</Button>
												</div>

												<div className='col-12 text-center my-2 d-flex align-items-center'>
													<hr className='flex-grow-1' />
													<span
														className='mx-3 text-muted'
														style={{ fontSize: '0.9rem' }}>
														{t('OR')}
													</span>
													<hr className='flex-grow-1' />
												</div>

												<div className='col-12 mb-5 d-flex justify-content-center'>
													<div className='google-login-container w-100'>
														<GoogleLogin
															onSuccess={handleSuccess}
															onError={handleError}
															useOneTap
															theme='outline'
															shape='rectangular'
															width='100%'
															size='large'
														/>
													</div>
												</div>
											</div>
										</>
									)}

									{/* BEGIN :: Social Login */}
									{!usernameInput && (
										<>
											{/* <div className='col-12 mt-3 text-center text-muted'>
												OR
											</div> */}
											{/* <div className='col-12 mt-3'>
												<Button
													isOutline
													color={darkModeStatus ? 'light' : 'dark'}
													className={classNames('w-100 py-3', {
														'border-light': !darkModeStatus,
														'border-dark': darkModeStatus,
													})}
													icon='CustomApple'
													onClick={handleOnClick}>
													Sign in with Apple
												</Button>
											</div> */}
											{/* <div className='col-12'>
												<Button
													isOutline
													color={darkModeStatus ? 'light' : 'dark'}
													className={classNames('w-100 py-3', {
														'border-light': !darkModeStatus,
														'border-dark': darkModeStatus,
													})}
													icon='CustomGoogle'
													onClick={handleOnClick}>
													Continue with Google
												</Button>
											</div> */}
										</>
									)}
									{/* END :: Social Login */}
								</form>
							</CardBody>
						</Card>
						<div
							className=''
							style={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								marginTop: '-27px',
							}}>
							<a
								href='landing/privacy'
								className={classNames('text-decoration-none me-3', {
									'link-light': effectiveIsNewUser,
									'link-dark': !effectiveIsNewUser,
								})}
								style={{ color: 'white' }}>
								{t('Privacy Policy')}
							</a>
							<a
								href='landing/terms'
								className={classNames('link-light text-decoration-none me-3', {
									'link-light': effectiveIsNewUser,
									'link-dark': !effectiveIsNewUser,
								})}
								style={{ color: 'white' }}>
								{t('Terms Of Use')}
							</a>
							{/* <a
								href='https://schedulerdesk.com/'
								className={classNames('link-light text-decoration-none', {
									'link-light': isNewUser,
									'link-dark': !isNewUser,
								})}
								style={{ color: 'white' }}>
								SchedulerDesk
							</a> */}
						</div>
						{/* </div> */}
						{/* </div> */}
					</Page>
				</PageWrapper>
			</div>
		</>
	);
};
Login.propTypes = {
	isSignUp: PropTypes.bool,
};
Login.defaultProps = {
	isSignUp: false,
};

export default Login;
